// <s-hero-spin> cycles stacked product angle shots so the shoe appears to turn.
// Children marked [data-spin-frame] are crossfaded; a [data-spin-toggle] button pauses and resumes (WCAG 2.2.2).
class HeroSpin extends HTMLElement {
  #timer = 0;
  #index = 0;
  #frames = [];
  #toggle = null;
  #video = null;
  #paused = { explicit: false, hover: false, focus: false, hidden: false, motion: false };
  #controller = null;

  connectedCallback() {
    this.#frames = Array.from(this.querySelectorAll('[data-spin-frame]'));
    this.#toggle = this.querySelector('[data-spin-toggle]');
    this.#video = this.querySelector('video');
    if (this.#frames.length < 2 && !this.#video) {
      if (this.#toggle) this.#toggle.hidden = true;
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#paused.motion = motionQuery.matches;

    motionQuery.addEventListener('change', (event) => {
      this.#paused.motion = event.matches;
      this.#sync();
    }, { signal });
    this.addEventListener('pointerenter', () => { this.#paused.hover = !this.#video; this.#sync(); }, { signal });
    this.addEventListener('pointerleave', () => { this.#paused.hover = false; this.#sync(); }, { signal });
    this.addEventListener('focusin', () => { this.#paused.focus = !this.#video; this.#sync(); }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) { this.#paused.focus = false; this.#sync(); }
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      this.#paused.hidden = document.hidden;
      this.#sync();
    }, { signal });
    this.#toggle?.addEventListener('click', () => {
      this.#paused.explicit = !this.#paused.explicit;
      this.#sync();
    }, { signal });

    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    window.clearInterval(this.#timer);
  }

  #sync() {
    const shouldRun = !Object.values(this.#paused).some(Boolean);
    window.clearInterval(this.#timer);
    if (this.#video) {
      if (shouldRun) {
        this.#video.muted = true;
        this.#video.play().catch(() => {});
      } else {
        this.#video.pause();
      }
    } else if (shouldRun) {
      const interval = Number(this.dataset.interval) || 1400;
      this.#timer = window.setInterval(() => this.#advance(), interval);
    }
    if (this.#toggle) {
      const pausedLabel = this.#toggle.dataset.playLabel;
      const playingLabel = this.#toggle.dataset.pauseLabel;
      this.#toggle.setAttribute('aria-label', this.#paused.explicit ? pausedLabel : playingLabel);
      this.#toggle.setAttribute('aria-pressed', String(this.#paused.explicit));
    }
  }

  #advance() {
    const current = this.#frames[this.#index];
    this.#index = (this.#index + 1) % this.#frames.length;
    const next = this.#frames[this.#index];
    next.classList.remove('opacity-0');
    current.classList.add('opacity-0');
  }
}

if (!customElements.get('s-hero-spin')) customElements.define('s-hero-spin', HeroSpin);
